//src/pages/gro/RiskInventoriesPage.tsx
import { useMemo, useState } from "react";
import {
  AlertTriangle,
  Archive,
  Eye,
  Plus,
  RotateCcw,
  ShieldCheck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { PermissionButton } from "@/components/shared/PermissionButton";
import { PermissionIconButton } from "@/components/shared/PermissionIconButton";
import { EmptyState } from "@/components/shared/EmptyState";
import { PageHeader } from "@/components/shared/PageHeader";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  useRiskInventories,
  useSetRiskInventoryStatus,
} from "@/hooks/useRiskInventories";
import { useUnits } from "@/hooks/useUnits";
import { useAuth } from "@/contexts/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import type { RiskInventory, RiskInventoryStatus } from "@/types/riskInventory";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function statusLabel(status: RiskInventoryStatus) {
  if (status === "draft") return "Rascunho";
  if (status === "active") return "Ativo";
  if (status === "approved") return "Aprovado";
  return "Arquivado";
}

function statusVariant(
  status: RiskInventoryStatus,
): "default" | "secondary" | "outline" | "destructive" {
  if (status === "active") return "default";
  if (status === "approved") return "secondary";
  return "outline";
}

export default function RiskInventoriesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const canRead =
    user?.permissions?.includes(PERMISSIONS.RISK_INVENTORY_READ) ?? false;
  const [statusFilter, setStatusFilter] = useState<
    "all" | "draft" | "active" | "approved" | "archived"
  >("all");
  const [unitId, setUnitId] = useState("");
  const [archiveTarget, setArchiveTarget] = useState<RiskInventory | null>(null);

  const unitsQ = useUnits();
  const units = unitsQ.data ?? [];
  const unitNameById = useMemo(
    () => Object.fromEntries(units.map((unit) => [unit.id, unit.name])),
    [units],
  );

  const listQ = useRiskInventories({
    status: statusFilter === "all" ? "" : statusFilter,
    unit_id: unitId || "",
  });

  const setStatusMut = useSetRiskInventoryStatus();
  const items = listQ.data ?? [];
  const busy = setStatusMut.isPending;

  async function changeStatus(item: RiskInventory, nextStatus: RiskInventoryStatus) {
    try {
      await setStatusMut.mutateAsync({
        inventoryId: item.id,
        status: nextStatus,
      });

      toast.success("Status atualizado com sucesso.");
      setArchiveTarget(null);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao alterar status.");
    }
  }

  function renderActions(item: RiskInventory) {
    return (
      <div className="flex items-center justify-end gap-1">
        <PermissionIconButton
          label="Visualizar"
          permission={PERMISSIONS.RISK_INVENTORY_READ}
          onClick={() => navigate(`/gro/risk-inventories/${item.id}`)}
        >
          <Eye className="h-4 w-4" />
        </PermissionIconButton>

        {item.status !== "active" ? (
          <PermissionIconButton
            label="Ativar"
            permission={PERMISSIONS.RISK_INVENTORY_WRITE}
            disabled={busy}
            onClick={() => changeStatus(item, "active")}
          >
            <ShieldCheck className="h-4 w-4" />
          </PermissionIconButton>
        ) : null}

        {item.status !== "draft" ? (
          <PermissionIconButton
            label="Rascunho"
            permission={PERMISSIONS.RISK_INVENTORY_WRITE}
            disabled={busy}
            onClick={() => changeStatus(item, "draft")}
          >
            <RotateCcw className="h-4 w-4" />
          </PermissionIconButton>
        ) : null}

        {item.status !== "archived" ? (
          <PermissionIconButton
            label="Arquivar"
            permission={PERMISSIONS.RISK_INVENTORY_WRITE}
            disabled={busy}
            onClick={() => setArchiveTarget(item)}
            destructive
          >
            <Archive className="h-4 w-4" />
          </PermissionIconButton>
        ) : null}
      </div>
    );
  }

  return (
    <div className="pb-10">
      <PageHeader
        title="Inventários de Riscos"
        description="Gerencie inventários e acompanhe os itens de risco."
      >
        <PermissionButton
          permission={PERMISSIONS.RISK_INVENTORY_WRITE}
          onClick={() => navigate("/gro/risk-inventories/novo")}
        >
          <Plus className="mr-2 h-4 w-4" />
          Novo Inventário
        </PermissionButton>
      </PageHeader>

      <Card className="mb-4 p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <Select
            value={statusFilter}
            onValueChange={(value) =>
              setStatusFilter(
                value as "all" | "draft" | "active" | "approved" | "archived",
              )
            }
          >
            <SelectTrigger className="w-full lg:w-[220px]">
              <SelectValue placeholder="Todos os status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="draft">Rascunho</SelectItem>
              <SelectItem value="active">Ativo</SelectItem>
              <SelectItem value="approved">Aprovado</SelectItem>
              <SelectItem value="archived">Arquivado</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={unitId || "__all__"}
            onValueChange={(value) => setUnitId(value === "__all__" ? "" : value)}
          >
            <SelectTrigger className="w-full lg:w-[260px]">
              <SelectValue placeholder="Todas as unidades" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todas as unidades</SelectItem>
              {units.map((unit) => (
                <SelectItem key={unit.id} value={unit.id}>
                  {unit.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {!canRead ? (
        <Card className="p-6 text-sm text-destructive">
          Você não possui permissão para visualizar inventários.
        </Card>
      ) : items.length === 0 ? (
        <EmptyState
          title="Nenhum inventário encontrado"
          description="Crie um inventário para consolidar riscos."
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Inventário</TableHead>
                <TableHead>Unidade</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Versão</TableHead>
                <TableHead>Itens</TableHead>
                <TableHead className="w-[180px] text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">{item.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {item.updated_at || "-"}
                      </span>
                    </div>
                  </TableCell>

                  <TableCell className="text-sm text-muted-foreground">
                    {item.unit_id
                      ? unitNameById[item.unit_id] || item.unit_id
                      : "Sem unidade"}
                  </TableCell>

                  <TableCell>
                    <Badge variant={statusVariant(item.status)}>
                      {statusLabel(item.status)}
                    </Badge>
                  </TableCell>

                  <TableCell>{item.version_label || "-"}</TableCell>

                  <TableCell>
                    <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                      <AlertTriangle className="h-4 w-4" />
                      {item.items_count}
                    </div>
                  </TableCell>

                  <TableCell className="text-right">{renderActions(item)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <ConfirmDialog
        open={!!archiveTarget}
        onOpenChange={(open) => !open && setArchiveTarget(null)}
        title="Arquivar inventário"
        description={
          archiveTarget
            ? `Ao arquivar “${archiveTarget.name}”, ele deixa de ser o inventário ativo operacional, mas o histórico e os itens continuam preservados. Deseja continuar?`
            : ""
        }
        onConfirm={() => archiveTarget && changeStatus(archiveTarget, "archived")}
        confirmLabel="Arquivar"
        variant="destructive"
      />
    </div>
  );
}
